
import UriTemplate from 'uri-templates';

import {Mutex} from "../utils/mutex";
import {BBModel} from "../bb_model";
import {context} from "../constants/context";
import {emitter} from "../utils/emitter";
import {BookingSet} from "../collection/sets/booking_set";


const basket = {

    /**
     * @description
     * Function used in place of addItem and updateBasket used by bookingbug-customer.
     */
    async reserveOrUpdateBooking({ company, client, basket }) {

        // Book link in this case is a product (service or event).
        let itemWithBookLink = basket.items.find(item => item.book_link);
        if (itemWithBookLink) {
            const mutex = await Mutex.getLock();
            try {
                const data = { entire_basket: true, items: basket.items.map(item => item.getPostData()) };
                const updatedBasketResource = await itemWithBookLink.book_link.$post('book', {}, data);
                Mutex.unlock(mutex);

                company.$flush('basket');

                const updatedBasket = new BBModel.Basket(updatedBasketResource);
                const itemsResource = await updatedBasket.$get('items');

                let promises = [];
                Array.from(itemsResource).forEach((itemResource) => {
                    const item = new BBModel.BasketItem(itemResource);
                    updatedBasket.addItem(item);

                    // Keep an eye on if this item needs any promises resolved to be valid.
                    promises = promises.concat(item.promises);
                });

                if (promises.length > 0) {
                    await Promise.all(promises);
                }

                emitter.emit('basket:updated', updatedBasket);
                return updatedBasket;
            } catch (error) {
                Mutex.unlock(mutex);
                return Promise.reject(error);
            }
        } else {
            return Promise.reject(`Link 'book' not found for chosen service or event.`);
        }
    },

    addItem(company, params) {
        return new Promise((resolve, reject) => {
            const lnk = params.item.book_link;
            const data = params.item.getPostData();
            if (!lnk) {
                reject('rel book not found for event');
            } else {
                Mutex.getLock().then(mutex =>
                    lnk.$post('book', params, data).then((basket) => {
                            Mutex.unlock(mutex);
                            company.$flush('basket');
                            const mbasket = new BBModel.Basket(basket, params.bb);
                            return basket.$get('items').then((items) => {
                                    let promises = [];
                                    for (const i of Array.from(items)) {
                                        const item = new BBModel.BasketItem(i, params.bb);
                                        mbasket.addItem(item);
                                        // keep an eye on if this item needs any promises resolved to be valid
                                        promises = promises.concat(item.promises);
                                    }
                                    if (promises.length > 0) {
                                        return Promise.all(promises).then(() => resolve(mbasket));
                                    } else {
                                        return resolve(mbasket);
                                    }
                                }
                                , err => reject(err));
                        }
                        , (err) => {
                            Mutex.unlock(mutex);
                            return reject(err);
                        })
                );
            }
        });
    },

    applyCoupon(company, params) {
        return new Promise((resolve, reject) => {

            Mutex.getLock().then(mutex =>
                company.$post('coupon', {}, {coupon: params.coupon}).then((basket) => {
                        Mutex.unlock(mutex);
                        company.$flush('basket');
                        const mbasket = new BBModel.Basket(basket, params.bb);
                        return basket.$get('items').then((items) => {
                                let promises = [];
                                for (const i of Array.from(items)) {
                                    const item = new BBModel.BasketItem(i, params.bb);
                                    mbasket.addItem(item);
                                    // keep an eye on if this item needs any promises resolved to be valid
                                    promises = promises.concat(item.promises);
                                }
                                if (promises.length > 0) {
                                    return Promise.all(promises).then(() => resolve(mbasket));
                                } else {
                                    return resolve(mbasket);
                                }
                            }
                            , err => reject(err));
                    }
                    , (err) => {
                        Mutex.unlock(mutex);
                        return reject(err);
                    })
            );
        });
    },

    // add several items at onece - params should have an array of items:
    updateBasket(company, params) {
        let lnk;
        return new Promise((resolve, reject) => {

            const data = {entire_basket: true, items: []};

            for (const item of Array.from(params.items)) {
                if (item.book_link) {
                    lnk = item.book_link;
                }
                const xdata = item.getPostData();
                // force the date into utc
                //      d = item.date.date._a
                //      date = new Date(Date.UTC(d[0], d[1], d[2]))
                //      xdata.date = date
                data.items.push(xdata);
            }

            if (!lnk) {
                reject('rel book not found for event');
                //return deferred.promise;
                return;
            }
            Mutex.getLock().then(mutex =>
                lnk.$post('book', params, data).then((basket) => {
                        Mutex.unlock(mutex);
                        company.$flush('basket');
                        const mbasket = new BBModel.Basket(basket, params.bb);
                        return basket.$get('items').then((items) => {
                                let promises = [];
                                for (const i of Array.from(items)) {
                                    const item = new BBModel.BasketItem(i, params.bb);
                                    mbasket.addItem(item);
                                    // keep an eye on if this item needs any promises resolved to be valid
                                    promises = promises.concat(item.promises);
                                }
                                if (promises.length > 0) {
                                    return Promise.all(promises).then(() => {
                                        //$rootScope.$broadcast('basket:updated', mbasket); TODO !!!
                                        emitter.emit('basket:updated', mbasket);
                                        return resolve(mbasket);
                                    });
                                } else {
                                    //$rootScope.$broadcast('basket:updated', mbasket); TODO !!!
                                    emitter.emit('basket:updated', mbasket);
                                    return resolve(mbasket);
                                }
                            }
                            , err => reject(err));
                    }
                    , (err) => {
                        Mutex.unlock(mutex);
                        return reject(err);
                    })
            );

        });
    },

    checkPrePaid(item, pre_paid_bookings) {
        let valid_pre_paid = null;
        for (const booking of Array.from(pre_paid_bookings)) {
            if (booking.checkValidity(item)) {
                valid_pre_paid = booking;
                break;
            }
        }
        return valid_pre_paid;
    },

    query(company, params) {
        return new Promise((resolve, reject) => {
            if (!company.$has('basket')) {
                reject('rel basket not found for company');
            } else {
                company.$get('basket').then((basket) => {
                        basket = new BBModel.Basket(basket, params.bb);
                        if (basket.$has('items')) {
                            basket.$get('items').then(items => Array.from(items).map(item => basket.addItem(new BBModel.BasketItem(item, params.bb))));
                        }
                        return resolve(basket);
                    }
                    , err => reject(err));
            }
        });
    },

    deleteItem(item, company, params) {
        if (!params) {
            params = {};
        }
        if (params.basket) {
            params.basket.clearItem(item);
        }
        return new Promise((resolve, reject) => {
            if (!item.$has('self')) {
                reject('rel self not found for item');
            } else {
                Mutex.getLock().then(mutex =>
                        item.$del('self', params).then((basket) => {
                                Mutex.unlock(mutex);
                                company.$flush('basket');
                                basket = new BBModel.Basket(basket, params.bb);
                                if (basket.$has('items')) {
                                    basket.$get('items').then(items =>
                                        (() => {
                                            const result = [];
                                            for (item of Array.from(items)) {
                                                result.push(basket.addItem(new BBModel.BasketItem(item, params.bb)));
                                            }
                                            return result;
                                        })()
                                    );
                                }
                                return resolve(basket);
                            }
                            , err => reject(err))

                    , (err) => {
                        Mutex.unlock(mutex);
                        return reject(err);
                    });
            }

        });
    },

    checkout(company, basket, params) {
        if (company.company && company.basket) {
            basket = company.basket;
            company = company.company;
            params = {bb: {}};
        }
        return new Promise((resolve, reject) => {
            if (!basket.$has('checkout')) {
                reject('rel checkout not found for basket');
            } else {
                const data = basket.getPostData();
                if (params.bb.qudini_booking_id) {
                    data.qudini_booking_id = params.bb.qudini_booking_id;
                }
                if (params.bb.booking_settings) {
                    data.booking_settings = params.bb.booking_settings;
                }
                if (params.bb.no_notifications) {
                    data.no_notifications = params.bb.no_notifications;
                }
                if (params.bb.email_config) {
                    data.email_config = params.bb.email_config;
                }
                data.affiliate_id = context.affiliate_id || params.affiliate_id;
                basket.waiting_for_checkout = true;
                Mutex.getLock()
                    .then((mutex) => {
                        this.mutex = mutex;

                        /*
                         * Sending items to the basket will result in an empty attendees
                         * array because we are overriding the basket
                         */
                        const dataWithoutItems = Object.assign({}, data);
                        delete dataWithoutItems.items;
                        return basket.$post('checkout', params, dataWithoutItems);
                    })
                    .then((total) => {
                        Mutex.unlock(this.mutex);
                        //$rootScope.$broadcast('updateBookings'); //TODO !!!
                        emitter.emit('updateBookings');
                        if (total.$has('admin_bookings') && company.bookings) {
                            total.$link('admin_bookings').forEach((booking_link) => {
                                const href = new UriTemplate(booking_link.href).fillFromObject({});
                                const push = {
                                    action: 'update',
                                    model: 'booking',
                                    group: 'bookings',
                                    url: href
                                }
                                company.bookings.handlePush(push);
                            });
                        }
                        this.purchaseTotalModel = new BBModel.Purchase.Total(total);
                        return this.purchaseTotalModel.getPurchaseData();
                    })
                    .then(() => {
                        //$rootScope.$broadcast('newCheckout', this.purchaseTotalModel); TODO !!!
                        emitter.emit('newCheckout', this.purchaseTotalModel);
                        basket.clear();
                        basket.waiting_for_checkout = false;
                        resolve(this.purchaseTotalModel);
                    })
                    .catch((err) => {
                        basket.waiting_for_checkout = false;
                        if (this.mutex) Mutex.unlock(this.mutex);
                        reject(err);
                    });
            }
        });
    },

    empty(bb) {
        return new Promise((resolve, reject) => {
            Mutex.getLock().then(mutex =>
                    bb.company.$del('basket').then((basket) => {
                            Mutex.unlock(mutex);
                            bb.company.$flush('basket');
                            return resolve(new BBModel.Basket(basket, bb));
                        }
                        , err => reject(err))

                , (err) => {
                    Mutex.unlock(mutex);
                    return reject(err);
                });
        });
    },

    memberCheckout(basket, params) {
        return new Promise((resolve, reject) => {
            if (!basket.$has('checkout')) {
                reject('rel checkout not found for basket');
            } else if (context.member === null) {
                reject('member not set');
            } else {
                basket._data.setOption('auth_token', context.member._data.getOption('auth_token'));
                const data = {items: (Array.from(basket.items).map(item => item._data))};
                basket.$post('checkout', params, data).then((total) => {
                        if (total.$has('member')) {
                            total.$get('member').then((member) => {
                                context.member.flushBookings();
                                return context.member = new BBModel.Member.Member(member);
                            });
                        }
                        return resolve(total);
                    }
                    , err => reject(err));
            }
        });
    },

    applyDeal(company, params) {
        return new Promise((resolve, reject) => {

            Mutex.getLock().then(mutex =>
                params.bb.basket.$post('deal', {}, {deal_code: params.deal_code}).then((basket) => {
                        Mutex.unlock(mutex);
                        company.$flush('basket');
                        const mbasket = new BBModel.Basket(basket, params.bb);
                        return basket.$get('items').then((items) => {
                                let promises = [];
                                for (const i of Array.from(items)) {
                                    const item = new BBModel.BasketItem(i, params.bb);
                                    mbasket.addItem(item);
                                    // keep an eye on if this item needs any promises resolved to be valid
                                    promises = promises.concat(item.promises);
                                }
                                if (promises.length > 0) {
                                    return Promise.all(promises).then(() => resolve(mbasket));
                                } else {
                                    return resolve(mbasket);
                                }
                            }
                            , err => reject(err));
                    }
                    , (err) => {
                        Mutex.unlock(mutex);
                        return reject(err);
                    })
            );
        });
    },

    removeDeal(company, params) {
        if (!params) {
            params = {};
        }
        return new Promise((resolve, reject) => {
            if (!params.bb.basket.$has('deal')) {
                return reject('No Remove Deal link found');
            } else {
                Mutex.getLock().then(mutex =>
                    params.bb.basket.$put('deal', {}, {deal_code_id: params.deal_code_id.toString()}).then((basket) => {
                            Mutex.unlock(mutex);
                            company.$flush('basket');
                            basket = new BBModel.Basket(basket, params.bb);
                            if (basket.$has('items')) {
                                return basket.$get('items').then((items) => {
                                        for (const item of Array.from(items)) {
                                            basket.addItem(new BBModel.BasketItem(item, params.bb));
                                        }
                                        return resolve(basket);
                                    }
                                    , err => reject(err));
                            }
                        }
                        , (err) => {
                            Mutex.unlock(mutex);
                            return reject(err);
                        })
                );

            }
        });
    }
};

export {
    basket
}
